// plataformaInstalacion.ts — ¿en qué teléfono/navegador estamos, para decidir CÓMO invitar a instalar la app? (round 2026-10-29)
//
// Función pura sobre el User-Agent: se prueba con cadenas reales (plataformaInstalacion.test.ts) y el único acceso al
// `navigator` está en `leerPlataformaActual()`.
//
// Por qué hace falta distinguir tanto en iPhone/iPad:
//   - Android (Chrome, Edge, Samsung Internet, Opera) tiene `beforeinstallprompt` y se instala con un botón; ahí NO se mira
//     el navegador (si el evento llega, se puede instalar; si no, no se muestra nada). Ver utils/instalacionPwa.ts.
//   - iOS no tiene ninguna API de instalación: solo se pueden dar instrucciones, y las instrucciones dependen de DÓNDE se
//     abrió la página. Safari trae «Agregar a pantalla de inicio» en el menú Compartir; desde iOS 16.4 también Chrome, Edge
//     y Firefox para iOS (antes de eso, solo Safari); y los navegadores EMBEBIDOS de otras apps (Instagram, Facebook,
//     TikTok…) no pueden instalar nada: hay que pedirle a la persona que abra la página en su navegador.
//
// Detalles del User-Agent que importan:
//   - iPadOS 13+ se presenta como un Mac («Macintosh; Intel Mac OS X»). Un Mac de verdad no tiene pantalla táctil, así que
//     «Macintosh» + `maxTouchPoints > 1` es un iPad. Este es el único caso donde el UA solo no alcanza.
//   - Chrome/Edge/Firefox en iOS son WebKit con su propio token (`CriOS`, `EdgiOS`, `FxiOS`) y TAMBIÉN traen «Safari/».
//   - Los navegadores embebidos de iOS (WKWebView) NO traen «Safari/»; se detectan por eso, y además por el token de la
//     app cuando existe (Instagram, FBAN/FBAV, Line, TikTok…) para poder nombrarla. El «Google» (GSA) sí trae «Safari/»,
//     por eso va en la lista de tokens.
//   - Lo que NO se puede detectar: WhatsApp y otras apps que abren los enlaces en un `SFSafariViewController` (en Android,
//     en una pestaña de Chrome personalizada). Se ven EXACTAMENTE como Safari/Chrome; ahí el aviso muestra las
//     instrucciones normales y la persona puede no encontrar la opción. No hay forma de saberlo desde la página.
//
// Esto es solo para decidir un texto de ayuda: ninguna decisión de seguridad ni de layout cuelga de acá.

export type NavegadorIos = "safari" | "chrome" | "edge" | "firefox" | "opera" | "otro" | "embebido";

export interface PlataformaInstalacion {
  /** Sistema del equipo. `otro` cubre móviles que no son ni Android ni iOS (no se les muestra nada). */
  sistema: "android" | "ios" | "escritorio" | "otro";
  /** Celular o tablet táctil. Es la puerta de todo el aviso: en escritorio nunca se invita. */
  esMovil: boolean;
  /** Solo en iOS: en qué navegador se abrió la página (define los pasos a mostrar). */
  navegadorIos: NavegadorIos | null;
  /** Nombre de la app que embebe el navegador (Instagram, Facebook…), si se pudo reconocer. */
  appEmbebida: string | null;
  /**
   * Versión de iOS como número (16.4 → 16.4), o `null` si no se puede saber. Chrome/Edge/Firefox pueden agregar a la
   * pantalla de inicio desde 16.4; con una versión menor o desconocida se avisa «si no ves la opción, ábrela en Safari».
   */
  versionIos: number | null;
}

/** Lo que se necesita del navegador; separado para poder probarlo con valores controlados. */
export interface EntradaPlataforma {
  userAgent: string;
  maxTouchPoints?: number;
  /** `navigator.userAgentData?.mobile` cuando el navegador lo expone (Chromium). */
  uaMobile?: boolean;
}

// Token de la app → nombre para mostrar. Orden: el primero que coincide gana.
const APPS_EMBEBIDAS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bInstagram\b/i, "Instagram"],
  [/\bFBAN\b|\bFBAV\b|\bFB_IAB\b|\bFBIOS\b/, "Facebook"],
  [/\bMessenger(?:ForiOS|Lite)?\b/i, "Messenger"],
  [/\bTikTok\b|musical_ly|BytedanceWebview/i, "TikTok"],
  [/\bLine\//, "Line"],
  [/\bSnapchat\b/i, "Snapchat"],
  [/\bTwitter\b/i, "X (Twitter)"],
  [/\bLinkedInApp\b/i, "LinkedIn"],
  [/\bPinterest\b/i, "Pinterest"],
  [/\bMicroMessenger\b/i, "WeChat"],
  [/\bKAKAOTALK\b/i, "KakaoTalk"],
  [/\bGSA\//, "Google"],
  [/\bWhatsApp\b/i, "WhatsApp"],
  [/\bTelegram\b/i, "Telegram"],
];

function nombreAppEmbebida(ua: string): string | null {
  for (const [patron, nombre] of APPS_EMBEBIDAS) if (patron.test(ua)) return nombre;
  return null;
}

/** «CPU iPhone OS 18_1», «CPU OS 17_0» (iPad) o, sin eso (iPad con UA de Mac), «Version/18.1» de Safari. */
function versionDeIos(ua: string): number | null {
  const os = /\bOS (\d+)[_.](\d+)/.exec(ua);
  if (os) return Number(`${os[1]}.${os[2]}`);
  const safari = /\bVersion\/(\d+)\.(\d+)/.exec(ua);
  // En iOS la versión de Safari coincide con la del sistema.
  return safari ? Number(`${safari[1]}.${safari[2]}`) : null;
}

function navegadorDeIos(ua: string, appEmbebida: string | null): NavegadorIos {
  // Un WKWebView no trae «Safari/»; las apps que sí lo traen (Google) se reconocen por su token.
  if (appEmbebida || !/\bSafari\//.test(ua)) return "embebido";
  if (/\bCriOS\//.test(ua)) return "chrome";
  if (/\bEdgiOS\//.test(ua)) return "edge";
  if (/\bFxiOS\//.test(ua)) return "firefox";
  if (/\bOPiOS\/|\bOPT\//.test(ua)) return "opera";
  if (/\bVersion\/\d/.test(ua)) return "safari";
  return "otro";
}

export function detectarPlataforma(entrada: EntradaPlataforma): PlataformaInstalacion {
  const ua = entrada.userAgent ?? "";
  const toques = entrada.maxTouchPoints ?? 0;

  const esAndroid = /\bAndroid\b/i.test(ua);
  // iPadOS con «Macintosh» en el UA: la pantalla táctil es lo que lo delata como iPad.
  const esIos = /\b(iPhone|iPod|iPad)\b/.test(ua) || (/\bMacintosh\b/.test(ua) && toques > 1);
  const esMovil = esAndroid || esIos || entrada.uaMobile === true;

  if (esIos) {
    const appEmbebida = nombreAppEmbebida(ua);
    return {
      sistema: "ios",
      esMovil: true,
      navegadorIos: navegadorDeIos(ua, appEmbebida),
      appEmbebida,
      versionIos: versionDeIos(ua),
    };
  }
  if (esAndroid) {
    return {
      sistema: "android",
      esMovil: true,
      navegadorIos: null,
      appEmbebida: nombreAppEmbebida(ua),
      versionIos: null,
    };
  }
  return {
    sistema: esMovil ? "otro" : "escritorio",
    esMovil,
    navegadorIos: null,
    appEmbebida: null,
    versionIos: null,
  };
}

/** La plataforma del navegador actual. Sin `navigator` (SSR, pruebas sin DOM) es escritorio: no se invita. */
export function leerPlataformaActual(): PlataformaInstalacion {
  if (typeof navigator === "undefined") return detectarPlataforma({ userAgent: "" });
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  return detectarPlataforma({
    userAgent: navigator.userAgent || "",
    maxTouchPoints: navigator.maxTouchPoints,
    uaMobile: uaData?.mobile,
  });
}

/** ¿Desde esta versión de iOS los navegadores que no son Safari pueden «Agregar a pantalla de inicio»? */
export const IOS_MINIMA_OTROS_NAVEGADORES = 16.4;
